// Token storage and refresh for the signed-in device session.
//
// Refresh tokens rotate, and presenting a rotated token again revokes the whole
// device session. Every tab shares one stored session. Each change to it
// (refresh, sign-in, sign-out) happens inside one cross-tab Web Lock, so a
// change is checked and written atomically against the other tabs. Refreshes
// are also single-flight within a tab. Inside the lock a tab re-reads storage
// and reuses a pair that another tab has just obtained.
import { createApiClient, type ApiClient, type Schemas } from "@10twenty/api";

export const SESSION_STORAGE_KEY = "tentwenty.session.v1";
const SESSION_LOCK = "tentwenty.session";
/** Refresh this long before the access token expires. */
const EXPIRY_SKEW_MS = 30_000;
const REFRESH_TIMEOUT_MS = 15_000;
const LOGOUT_TIMEOUT_MS = 10_000;
/** Longest wait for another tab's refresh (bounded by REFRESH_TIMEOUT_MS) to finish. */
const DEFAULT_LOCK_TIMEOUT_MS = 20_000;

export interface StoredSession {
  userId: string;
  deviceId: string;
  accessToken: string;
  /** Epoch milliseconds. */
  accessExpiresAt: number;
  refreshToken: string;
  refreshExpiresAt: number;
}

export type KeyValueStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export interface SessionDeps {
  /** Server origin plus `/api/v1`. */
  baseUrl: string;
  storage: KeyValueStorage;
  fetch: (request: Request) => Promise<Response>;
  /** Cross-tab lock; without it changes are only serialized within this instance. */
  locks?: Pick<LockManager, "request"> | undefined;
  now?: () => number;
  lockTimeoutMs?: number;
}

/** `AbortSignal.timeout`, which Safari has only from 16 (the iPhone SE's last iOS is 15.8). */
export function timeoutSignal(ms: number): AbortSignal {
  if (typeof AbortSignal.timeout === "function") return AbortSignal.timeout(ms);
  const controller = new AbortController();
  setTimeout(() => controller.abort(new DOMException("The operation timed out.", "TimeoutError")), ms);
  return controller.signal;
}

export function sessionFromTokens(userId: string, deviceId: string, tokens: Schemas["TokenPair"]): StoredSession {
  return {
    userId,
    deviceId,
    accessToken: tokens.access_token,
    accessExpiresAt: Date.parse(tokens.access_token_expires_at),
    refreshToken: tokens.refresh_token,
    refreshExpiresAt: Date.parse(tokens.refresh_token_expires_at),
  };
}

function parseSession(raw: string | null): StoredSession | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<StoredSession> | null;
    if (
      value &&
      typeof value.userId === "string" &&
      typeof value.deviceId === "string" &&
      typeof value.accessToken === "string" &&
      typeof value.refreshToken === "string" &&
      Number.isFinite(value.accessExpiresAt) &&
      Number.isFinite(value.refreshExpiresAt)
    ) {
      return value as StoredSession;
    }
  } catch {
    // Corrupt entry: treat as signed out.
  }
  return null;
}

export class SessionManager {
  private readonly deps: Required<Omit<SessionDeps, "locks">> & Pick<SessionDeps, "locks">;
  private readonly publicApi: ApiClient;
  private readonly listeners = new Set<() => void>();
  private inflight: { deviceId: string; promise: Promise<StoredSession | null> } | null = null;
  /** In-tab queue standing in for the Web Lock when it is unavailable. */
  private localQueue: Promise<unknown> = Promise.resolve();
  /** Cached for useSyncExternalStore, which needs a stable snapshot. */
  private snapshot: StoredSession | null;
  private snapshotRaw: string | null;

  constructor(deps: SessionDeps) {
    this.deps = { now: Date.now, lockTimeoutMs: DEFAULT_LOCK_TIMEOUT_MS, ...deps };
    this.publicApi = createApiClient({ baseUrl: deps.baseUrl, fetch: deps.fetch });
    this.snapshotRaw = this.deps.storage.getItem(SESSION_STORAGE_KEY);
    this.snapshot = parseSession(this.snapshotRaw);
  }

  /** The stored session, or null when signed out. */
  current(): StoredSession | null {
    const raw = this.deps.storage.getItem(SESSION_STORAGE_KEY);
    if (raw !== this.snapshotRaw) {
      this.snapshotRaw = raw;
      this.snapshot = parseSession(raw);
    }
    return this.snapshot;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Call when storage changed outside this instance (another tab). */
  notifyExternalChange(): void {
    this.emit();
  }

  /** Store a new sign-in. Use outside the lock only in tests. */
  set(session: StoredSession): void {
    this.deps.storage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
    this.emit();
  }

  /** Store the session returned by a sign-in or sign-up. */
  async signIn(session: StoredSession): Promise<void> {
    try {
      await this.withLock(() => {
        this.set(session);
        return Promise.resolve();
      });
    } catch {
      // Another tab is stuck holding the lock; the user still expects to be signed in.
      this.set(session);
    }
  }

  /**
   * The stored session with a usable access token, refreshing first if it is
   * about to expire. Null when signed out. Throws on network failure (the
   * session is kept).
   */
  async activeSession(): Promise<StoredSession | null> {
    const session = this.current();
    if (!session || this.isFresh(session)) return session;
    return this.refresh(session);
  }

  /**
   * Rotate the token pair of `stale`'s device session. Returns that session if
   * another caller (or tab) already rotated it. Returns null if the server
   * rejected it (which signs it out), or if it is no longer the stored session
   * (signed out, or replaced by another sign-in); a pair obtained for a
   * replaced session is discarded.
   */
  refresh(stale: StoredSession): Promise<StoredSession | null> {
    if (this.inflight?.deviceId === stale.deviceId) return this.inflight.promise;
    const promise = this.withLock(() => this.refreshLocked(stale)).finally(() => {
      if (this.inflight?.promise === promise) this.inflight = null;
    });
    this.inflight = { deviceId: stale.deviceId, promise };
    return promise;
  }

  /**
   * Forget this browser's session and revoke it on the server (best effort,
   * with time limits). A sign-in stored meanwhile by another tab is kept.
   */
  async signOut(): Promise<void> {
    const session = this.current();
    if (!session) return;
    let accessToken: string | null = null;
    try {
      accessToken = await this.withLock(async () => {
        const current = this.current();
        if (current?.deviceId !== session.deviceId) return null;
        let token: string | null = current.accessToken;
        if (!this.isFresh(current)) {
          try {
            token = (await this.refreshLocked(current))?.accessToken ?? null;
          } catch {
            token = null; // Offline: the server session expires on its own.
          }
        }
        this.clearIfDevice(session.deviceId);
        return token;
      });
    } catch {
      // The lock is stuck in another tab: forget the session locally anyway.
      this.clearIfDevice(session.deviceId);
    }
    if (!accessToken) return;
    try {
      await this.publicApi.POST("/auth/logout", {
        headers: { Authorization: `Bearer ${accessToken}` },
        signal: timeoutSignal(LOGOUT_TIMEOUT_MS),
      });
    } catch {
      // Best effort, as above.
    }
  }

  private isFresh(session: StoredSession): boolean {
    return session.accessExpiresAt - this.deps.now() > EXPIRY_SKEW_MS;
  }

  /** Runs `fn` holding the session lock; rejects if it isn't granted in time. */
  private withLock<T>(fn: () => Promise<T>): Promise<T> {
    const locks = this.deps.locks;
    if (locks) {
      const signal = timeoutSignal(this.deps.lockTimeoutMs);
      return locks.request(SESSION_LOCK, { signal }, fn) as Promise<T>;
    }
    const run = this.localQueue.then(fn);
    this.localQueue = run.catch(() => undefined);
    return run;
  }

  /** Call only while holding the session lock. */
  private async refreshLocked(stale: StoredSession): Promise<StoredSession | null> {
    const session = this.current();
    if (session?.deviceId !== stale.deviceId) return null;
    if (session.accessToken !== stale.accessToken && this.isFresh(session)) return session;
    if (session.refreshExpiresAt <= this.deps.now()) {
      this.clear();
      return null;
    }

    const { data, response } = await this.publicApi.POST("/auth/refresh", {
      body: { refresh_token: session.refreshToken },
      signal: timeoutSignal(REFRESH_TIMEOUT_MS),
    });
    // Defensive: every mutation holds the lock, but never resurrect or overwrite.
    if (this.current()?.refreshToken !== session.refreshToken) return null;
    if (data) {
      const next = sessionFromTokens(session.userId, session.deviceId, data);
      this.set(next);
      return next;
    }
    if (response.status === 400 || response.status === 401) {
      this.clear();
      return null;
    }
    throw new Error(`Token refresh failed (HTTP ${response.status})`);
  }

  private clearIfDevice(deviceId: string): void {
    if (this.current()?.deviceId === deviceId) this.clear();
  }

  private clear(): void {
    this.deps.storage.removeItem(SESSION_STORAGE_KEY);
    this.emit();
  }

  private emit(): void {
    for (const listener of this.listeners) listener();
  }

  /**
   * `fetch` for the authenticated API client: attaches the bearer token and,
   * on a 401, refreshes once and retries the request. The retry stays within
   * the original device session, never another account's.
   */
  readonly authFetch = async (request: Request): Promise<Response> => {
    const session = await this.activeSession();
    if (!session) return this.deps.fetch(request);
    const retry = request.clone();
    request.headers.set("Authorization", `Bearer ${session.accessToken}`);
    const response = await this.deps.fetch(request);
    if (response.status !== 401) return response;

    const refreshed = await this.refresh(session);
    if (!refreshed) return response;
    retry.headers.set("Authorization", `Bearer ${refreshed.accessToken}`);
    return this.deps.fetch(retry);
  };
}
